import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import { useAccountSettingEntries } from './presentation';
import { SettingCategory } from './Setting';
import { SettingsGroupDetail } from './SettingsGroupDetail';
import { READ_ONLY_CATEGORY, SETTINGS_GROUPS, getSettingsGroup } from './settingsGroups';
import { SettingsOverviewHub } from './SettingsOverviewHub';
import { settingsHubStyles as styles } from './settingsHubStyles';
import { SettingsSearchResults } from './SettingsSearchResults';

interface IProps {
	canModifyAccount?: boolean;
	hasDevPermission?: boolean;
	hasSupportPermission?: boolean;
	/** Mirrors the gate the Permanent column used to carry, as a value so cards and pills agree. */
	canSeePermanentSettings?: boolean;
	settingsSearch?: string;
	onClearSettingsSearch?: () => void;
	onClearAllDonations?: () => void;
	onOpenDonationTagSettings?: () => void;
	onOpenServiceJobTagSettings?: () => void;
}

/**
 * The settings tab's three views: an overview of the groups, one group in detail, or search
 * results across all of them. Which one shows is derived — a search wins, then a chosen group.
 *
 * Must only be mounted once the account has loaded; the row builder reads `account.id` unguarded.
 */
export function AccountSettingsSections({
	canModifyAccount,
	hasDevPermission,
	hasSupportPermission,
	canSeePermanentSettings,
	settingsSearch,
	onClearSettingsSearch,
	onClearAllDonations,
	onOpenDonationTagSettings,
	onOpenServiceJobTagSettings,
}: IProps) {
	const { entries, modals } = useAccountSettingEntries({
		canModifyAccount,
		hasDevPermission,
		hasSupportPermission,
		onClearAllDonations,
		onOpenDonationTagSettings,
		onOpenServiceJobTagSettings,
	});

	// In the URL so an admin can link someone straight to the group being discussed.
	//
	// Pushed rather than replaced: opening a group reads as a navigation, so the browser's Back
	// button has to come back to the hub. nuqs replaces by default, which left no entry to go back
	// to — Back skipped the hub and took the whole account tab with it.
	const [groupSlug, setGroupSlug] = useQueryState(
		'settingsGroup',
		parseAsString.withDefault('').withOptions({ history: 'push' })
	);

	const groups = SETTINGS_GROUPS.filter(group => group.category !== READ_ONLY_CATEGORY || !!canSeePermanentSettings);

	const query = settingsSearch?.trim() ?? '';
	// Falls back to the overview when the slug is unknown, or names a group this user cannot see.
	const selectedGroup = groups.find(group => group.slug === groupSlug);

	const openGroup = (category: SettingCategory) => setGroupSlug(getSettingsGroup(category)?.slug ?? null);

	return (
		<>
			<div className={css(styles.view)}>
				{query ? (
					<SettingsSearchResults
						entries={entries}
						groups={groups}
						query={query}
						onClear={() => onClearSettingsSearch?.()}
					/>
				) : selectedGroup ? (
					<SettingsGroupDetail
						entries={entries}
						group={selectedGroup}
						groups={groups}
						onOpenGroup={openGroup}
						onBackToOverview={() => setGroupSlug(null)}
					/>
				) : (
					<SettingsOverviewHub entries={entries} groups={groups} onOpenGroup={openGroup} />
				)}
			</div>
			{modals}
		</>
	);
}
